import { TagDisplay } from '@coop/components';
import classNames from 'classnames';
import { type PropsWithChildren, type ReactNode } from 'react';

import type { PriceData, PriceType } from '../../../common/models/priceData/priceData';

import { Helpers } from '../../../../utility/helpers';
import A11yTextWrapper, {
    type A11yTextType,
} from '../../../common/components/A11yTextWrapper/A11yTextWrapper';
import styles from './ProductInfoProperty.module.scss';
import {
    getProductCustomerInformationText,
    getProductOriginalPriceText,
} from './ProductInfoProperty.utils';

interface ProductInfoPropertyBaseProps {
    className?: string;
    a11yType: A11yTextType;
}

interface ProductInfoPropertyBaseWrapperProps {
    className?: string;
    a11yText?: string;
    a11yType: A11yTextType;
    children: string;
    underlined?: boolean;
    size?: 'default' | 'large';
}

interface TagInfoPropertyProps {
    className?: string;
    a11yText?: string;
    a11yType: A11yTextType;
    theme: React.ComponentProps<typeof TagDisplay>['theme'];
}

const OriginalPrice = (
    props: {
        priceType: PriceType;
        product: {
            priceData: PriceData | undefined;
        };
    } & ProductInfoPropertyBaseProps,
) => {
    const texts = getProductOriginalPriceText(props.product, props.priceType);
    if (!texts?.visual) return null;

    return (
        <BaseInfoProperty
            className={props.className}
            a11yType={props.a11yType}
            a11yText={texts.a11y}
            color="black"
        >
            {Helpers.appendDot(texts.visual)}
        </BaseInfoProperty>
    );
};

const CustomerInformationText = (
    props: ProductInfoPropertyBaseProps & {
        product: {
            consumerInformationText: string[] | undefined;
        };
    },
) => {
    const texts = getProductCustomerInformationText(props.product);
    if (!texts?.visual) return null;

    return (
        <BaseInfoProperty
            className={props.className}
            a11yType={props.a11yType}
            a11yText={texts.a11y}
            color="black"
        >
            {Helpers.appendDot(texts.visual)}
        </BaseInfoProperty>
    );
};

const BaseInfoProperty = (props: {
    className?: string;
    color: 'red' | 'black';
    size?: 'default' | 'large';
    underlined?: boolean;
    children: ReactNode;
    a11yText?: string;
    a11yType: A11yTextType;
}) => {
    return (
        <A11yTextWrapper
            a11yText={props.a11yText}
            a11yType={props.a11yType}
            className={classNames(
                styles.InfoProperty,
                props.underlined && styles.Underlined,
                props.color === 'red' && styles.Red,
                props.color === 'black' && styles.Black,
                props.size === 'large' && styles.Large,
                props.className,
            )}
        >
            {props.children}
        </A11yTextWrapper>
    );
};

const Red = (props: PropsWithChildren<ProductInfoPropertyBaseWrapperProps>) => {
    return (
        <BaseInfoProperty
            className={classNames(styles.Red, props.className)}
            a11yType={props.a11yType}
            a11yText={props.a11yText}
            underlined={props.underlined}
            size={props.size}
            color="red"
        >
            {Helpers.appendDot(props.children)}
        </BaseInfoProperty>
    );
};

const Black = (props: PropsWithChildren<ProductInfoPropertyBaseWrapperProps>) => {
    return (
        <BaseInfoProperty
            className={props.className}
            a11yType={props.a11yType}
            a11yText={props.a11yText}
            underlined={props.underlined}
            size={props.size}
            color="black"
        >
            {Helpers.appendDot(props.children)}
        </BaseInfoProperty>
    );
};

const TagInfoProperty = (props: PropsWithChildren<TagInfoPropertyProps>) => {
    return (
        <BaseInfoProperty
            className={props.className}
            a11yType={props.a11yType}
            a11yText={props.a11yText}
            color="black"
        >
            <TagDisplay theme={props.theme}>{props.children}</TagDisplay>
        </BaseInfoProperty>
    );
};

const ProductInfoProperty = {
    OriginalPrice,
    CustomerInformationText,
    Base: BaseInfoProperty,
    Red,
    Black,
    Tag: TagInfoProperty,
};

export default ProductInfoProperty;
